import {
  createRepositories,
  type Clock,
  type ExportFile,
  type ExportResult,
  type FileExporter,
  type IdGenerator,
  type OpenResult,
  type Repositories,
  type Storage,
} from '@anytracker/core'
import { createContext, useContext, useMemo, type ReactNode } from 'react'

/**
 * Everything platform-specific, in one place, injected at the root (T-201).
 *
 * This is the seam ADR-0002 is about. Screens take `Storage`, `Clock` and
 * `IdGenerator` — core's interfaces — and never import a concrete adapter, so:
 *
 *   - every screen is testable against `InMemoryStorage` with no IndexedDB and
 *     no mocking framework, and
 *   - the native app supplies SQLite implementations here and the screens it
 *     reuses do not change.
 *
 * The rule this enforces is narrow and worth stating plainly: **no component
 * below this boundary may import from `../adapters/`.** Doing so compiles, works
 * in the browser, and quietly removes both of those properties.
 */
export interface Services {
  readonly storage: Storage
  readonly clock: Clock
  readonly ids: IdGenerator
  readonly exporter: FileExporter
  readonly repos: Repositories
}

const ServicesContext = createContext<Services | null>(null)

/**
 * The exporter used when none was supplied, which refuses rather than pretends.
 *
 * Two screens out of a dozen export anything, so requiring every test to
 * construct an exporter would be noise. Defaulting to a silent no-op would be
 * worse than noise: an export that reports success and produces no file is
 * indistinguishable from a browser blocking the download, and this app's whole
 * durability story rests on backups actually happening (ADR-0003).
 *
 * Production cannot reach this. `App` takes the exporter as a **required**
 * prop, so `main.tsx` fails to compile if it forgets one.
 */
const refusingExporter: FileExporter = {
  save: (file: ExportFile): Promise<ExportResult> =>
    Promise.resolve({
      status: 'failed',
      reason: `No file exporter is configured, so ${file.filename} was not saved.`,
    }),
  open: (): Promise<OpenResult> =>
    Promise.resolve({
      status: 'failed',
      reason: 'No file exporter is configured, so no file could be opened.',
    }),
}

interface ProviderProps {
  readonly storage: Storage
  readonly clock: Clock
  readonly ids: IdGenerator
  readonly exporter?: FileExporter
  readonly children: ReactNode
}

export function ServicesProvider({
  storage,
  clock,
  ids,
  exporter = refusingExporter,
  children,
}: ProviderProps) {
  const services = useMemo<Services>(
    () => ({
      storage,
      clock,
      ids,
      exporter,
      repos: createRepositories({ storage, clock, ids }),
    }),
    [storage, clock, ids, exporter],
  )

  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>
}

/**
 * Throws when used outside the provider rather than returning `null`.
 *
 * A hook that can return null pushes a `?.` into every call site, and the one
 * place someone forgets it is a crash in front of the user. Failing at the
 * boundary makes the mistake a development-time one.
 */
export function useServices(): Services {
  const services = useContext(ServicesContext)
  if (!services) {
    throw new Error('useServices must be used inside a <ServicesProvider>')
  }
  return services
}

/** The repositories, which is what screens actually want most of the time. */
export function useRepositories(): Repositories {
  return useServices().repos
}
